import {LocalizationSpec} from "src/i18n/localizationSpec";

export default <LocalizationSpec>{
    title: "FALO",
    fullTitle: "Football Advanced League Organizer",
    footerHomePage: "With the kind assistance of",
    navigation: {
        home: "Home",
        tournaments: "My Tournaments",
        newTournament: "New Tournament",
        settings: "Settings",
        info: "Info",
        table: "Overview",
        round: "Matches",
        icon: "Icon",
    },
    logOut: "Log Out",
    logIn: "Log In",
    register: "Register",
    inactivity: "You have been logged out due to inactivity. Please log in again.",
    home: {
        title: "Welcome to",
        subtitle: "Your planer for football events for kids",
        introText: "FALO is a software that organizes youth football tournaments with mixed game formats, as specified by the DFB in the F-Youth category. Up to 50 children per event take turns playing: ",
        threeBullet: "**3v3 games** on 4 mini-goals, and",
        fiveBullet: "**5v5 games** (4+1) on small field goals.",
        specialRules: "The special features:",
        specialRuleOne: "There is no fixed tournament schedule. After each round, an algorithm calculates the next matchups based on the previous results.",
        specialRuleTwo: "On the 5v5 fields, two 3-player teams from **the same club merge** into a 5-player team with a substitute.",
        specialRuleThree: "The assignment ensures as balanced games as possible, and each child plays roughly the same number of 3v3 and 5v5 games.",
        letsgo: "Let's go! 🚀",
        manageButton: "Manage your tournaments",
        addTournamentButton: "Add a new tournament!",
    },
    tournaments: {
        title: "Your tournaments",
        selectTournamentButton: "Go to tournament",
        today: "Today",
        upcoming: "Upcoming",
        past: "Past",
    },
    editTournament: {
        title: "Edit tournament",
        cancel: "Cancel",
        update: "Update",
        saved: "Update successful",
        inputs: {
            startDateTime: "Start Time *",
            address: "Address *",
        }
    },
    deleteTournament: {
        title: "Are you sure?",
        cancel: "Cancel",
        delete: "Delete",
        question: "\"{tournamentName}\" and all its associated teams and matches will be permanently deleted.",
        deleted: "Delete successful",
    },
    addTournament: {
        title: "Add a new tournament!",
        inputs: {
            tournamentName: "Tournament name *",
            street: "Street *",
            streetNumber: "Street number *",
            postalCode: "Postal code *",
            city: "City *",
            date: "Date *",
            startTime: "Start time *",
            endDate: "End Date",
            endTime: "End time",
            duration: "Duration [min]",
            numberTeams: "Expected number of teams *",
            numberRounds: "Number of rounds *",
            rules: {
                typeSomething: "Type something...",
                maxLength: "The name is too long.",
                streetNumberRule: "Street number has to be a natural number.",
                postalCodeRule: "Not a valid postal code.",
                numberTeamsEvenRule: "Number of teams has to be even.",
                numberTeamsMinMax: "Number of teams has to between 6 and 16."
            }
        },
        acceptDataHandling: "I agree that the tournament data can be viewed by anyone who knows the link to the tournament.",
        tableVisibleToggle: "Show leaderboard for all",
        addTournamentButton: "Add tournament",
        resetTournamentButton: "Reset",
        tournamentSaved: "Tournament added successfully!"
    },
    tournamentInfo: {
        title: "Tournament Info",
        organizer: "Organizer",
        location: "Location",
        date: "Date",
        time: "Time",
        timeSuffix: "",
        duration: "Duration",
        numberTeams: "Expected number of teams",
        numberTeamsShort: "teams expected",
        addTeamsButton: "Club",
        startTournamentButton: "Start",
        startTournamentTooltip: "The number of teams does not match",
        startTournamentSuccess: "The tournament was started successfully!",
        tournamentAlreadyStarted: "The tournament has already started",
        notEnoughTeamsGoHeader: "Less teams than expected",
        notEnoughTeamsGoInfo: "The current number of teams does not match the expectation. Do you still want to start the tournament?",
        genericWarning: "Tournament requirements not fulfilled",
        genericWarningInfo: "Edit the teams in this tournament, such that the following requirements are fulfilled:",
        tooManyTeams: {
            bullet: "Maximum expected number of teams",
            header: "Too many teams",
            info: "Remove teams or adjust the expected number of teams. The following conditions must be met:",
            validationInfoHeader: "There are more teams than planned?",
            validationInfoText: "The number of teams can still be adjusted. To do so, use the “Edit” icon to the right of the tournament name under the “Info” tab.",
        },
        lessThanSix: {
            bullet: "At least 6 teams",
            header: "Too few teams",
            info: "Add more teams so that the following conditions apply:",
            validationInfoHeader: "Only 4 teams?",
            validationInfoText: "Then why play FALO? You could just play a 5v5 game.",
        },
        someClubLessThanTwoTeams: {
            bullet: "At least 2 teams per club",
            header: "Not enough teams in a club",
            info: "Note that at least 2 teams from each club must participate in the tournament. The following tournament conditions must be met.",
            validationInfoHeader: "Help, a club has fewer than 5 children...",
            validationInfoText: "Clubs with fewer than five children can merge with another club and field another team of three there. " +
                "Funino games continue to be played independently, while in classic games, children can form a team of five together with a trio from the other club.",
        },
        atLeastTwoClubs: {
            bullet: "At least 2 clubs",
            header: "Too few clubs",
            info: "Add more clubs so that the following tournament conditions are met:",
            validationInfoHeader: "Only one club?",
            validationInfoText: "In this case, a training would be a much better idea than a tournament 😉.",
        },
        oddNumberOfTeams: {
            bullet: "Even number of teams",
            header: "Odd number of teams",
            info: "The number of teams in the tournament must be even, so that the following conditions apply:",
            validationInfoHeader: "Odd number of trios?",
            validationInfoText: "An odd total number of three-player teams is unfortunately not possible at the moment. In this case, the following procedure can be used:\n\n" +
                "**a)** Split a single team of 4 children into two teams of 2 children each. If these two \"two-player teams\" later merge on a 5v5 field, it becomes a 4v4 match, and the opposing team also plays with only 4 children.\n\n" +
                "**b)** If there is no team with 4 children, split two \"three-player teams\" from one club into three \"two-player teams.\"",
        },
        tooManyTeamsInOneClub: {
            bullet: "No more than half of the teams from one club",
            header: "Too many teams in one club",
            info: "The division of teams in the tournament must consider the following requirements:",
            validationInfoHeader: "One club provides more than half of the teams?",
            validationInfoText: "This is problematic because some children would play against their own club too often in this case. As a workaround, the club could be divided into two clubs (e.g., “red” and “white” or ‘A’ and “B”) for this tournament.",
        },
        startTournamentInfo: "Before starting the tournament, make sure the participating clubs are correct. Editing clubs after the tournament has started will cause the game schedule to be recalculated.",
        startTournament: "Start the tournament",
        shareTournament: {
            title: "Share",
            header: "Share this tournament",
            info: "Send this link to participants and interested people. You can directly copy and forward it here.",
            fail: "Copied failed",
            success: "Copy successful.",
        },
        noGoalDifferenceHeader: "Why no scores?",
        noGoalDifferenceTextLong: "You won't find goal differences here! Results are recorded only as a win, draw, or loss. Why? This is the only way to ensure everyone follows the \"gentleman's agreement\": If a team is ahead by 3 or more goals, they must remove one player. Alternatively, the team that is behind can play with one extra player until the difference drops below 3 goals. This rule applies to both 3v3 Funino games and classic 5v5 games.\n\n" +
            "As soon as all the results of a round have been entered, the next game round is automatically determined.",
        noGoalDifferenceText: "You won't find goal differences here! Results are recorded only as a win, draw, or loss. Why? This is the only way to ensure everyone follows the gentleman's agreement: If a team is ahead by 3 or more goals, they must remove one player. Alternatively, the team that is behind can play with one extra player until the difference drops below 3 goals. This rule applies to both 3v3 Funino games and classic 5v5 games.",
        teamStrengthInfoHeader: "Why team strengths?",
        teamStrengthInfoText: "Please ask each participating coach to rate the strength of their teams on a scale from 1 (beginner) to 10 (very strong). You can adjust these ratings up until just before the tournament starts using the \"TEAMS\" button. These ratings help the algorithm create balanced matchups for the first two rounds. Afterwards, the ratings have no effect on the tournament.",
        shortClubNameHeader: "Hint for the short club name",
        shortClubNameText: "Specify here how the teams should be displayed in the tournament. This can, for example, be the region or city name.",
        threePersonTeamsHeader: "3 kids per team",
        threePersonTeamsText: "A single three-player team per club can also consist of 2 or 4 children. If a team with only 2 children plays on a 3v3 mini-field, it becomes a 2v2 game, and players on the opposing team will rotate. You can still adjust the number of three-player teams per club until shortly before the tournament starts.\n\n" +
            "**Hint**: Each club needs at least two three-player teams, otherwise it couldn’t play 5v5 games. Additionally, a club may supply at most half of all teams in the tournament, otherwise it would end up playing against itself too often.",
        expectedNumberOfTeamsHeader: "Expected number of three player teams",
        expectedNumberOfTeamsText: "How many three-player teams do you expect in total? You can adjust the actual number later. It just needs to be even and no greater than 16.",
        expectedNumberOfRoundsHeader: "Oops! This Function is not available yet.",
        expectedNumberOfRoundsText: "The number of rounds cannot be changed at the moment. It depends on the number of teams in the tournament. Therefore, the total playing time for the children can only be controlled via the round duration. Recommended guidelines can be found in the following table:",
        numberOfPlayers: "Number of teams",
        numberOfRounds: "Number of rounds",
        numberOfMinutes: "Recommended round duration",
        tableVisibleToggle: "Show leaderboard for all",
    },
    tournamentTable: {
        title: "Overview",
        columns: {
            team: "Team",
            rank: "POS",
            points: "PTS",
            numberMatches: "P",
            numberFunino: "F",
            numberClassic: "C",
        },
        legend: {
            matches: "Matches",
            funino: "Funino",
            classic: "Classic"
        },
        noTeamsAddedYet: "No teams added yet",
        gamesDisplayButton: "Results",
        tableDisplayButton: "Table",
        gamesDisplay: {
            tbd: "TBD",
            round: "Round",
            win: "Won",
            draw: "Draw",
            page: "Page",
            noGames: "No results available yet",
            vs: "vs.",
        }
    },
    tournamentRound: {
        title: "Matches",
        round: "Round ",
        submitMatchResultDialogTitle: "Who won?",
        draw: "Draw",
        submitMatchResult: "Submit",
        submitMatchResultButtonHover: "All match results have to be determined",
        matchesCanNoLongerBeChanged: "Results can no longer be edited",
        noMatchesGenerated: "No matches have been generated yet",
        confirmMatchResult: "Confirm",
        submitMatchResultTitle: "Submit results and calculate the next round",
        submitMatchResultQuestion: "Are you sure that the results are correct? Changing the results later on will cause the game schedule to be recalculated.",
        savedMatchResult: "Result saved successfully.",
        errorSavingMatchResult: "You already submitted results for these games!",
        startNewRound: "New round started.",
        errorStartNewRound: "Ooops, the new matches couldn't be determined...",
        finishedTournamentMessage: "Tournament finished successfully!",
        firstRoundCalculation: "Calculate Round 1",
        warningTitle: "Resubmit the results",
        warningPreviousRoundChangeNextRound: "Are you sure you want to proceed? The table will be updated, and future rounds will be recalculated.",
        recalculatedNextRound: "Game state updated successfully",
        pitchSetup: "Pitch Setup",
        setupHintsLabel: "Setup hints",
        setupHints: "**Setup**: Set up the playing fields as shown. For 6 or 8 teams, only the upper half of the field is sufficient. " +
            "The dimensions are approximately 40x25m for the 5v5 small fields and 24x20m for the 3v3 Funino fields. \n" +
            "\n" +
            "**Hint**: It is best to set up a 3v3 field with exactly 8 cones (4 corner points and 4 goal shooting zone markings 8m from the baseline). " +
            "This allows it to be quickly converted into a smaller 2v2 field (20x16m) by moving the two mini goals from one baseline 8m to " +
            "the goal shooting zone. The other shooting zone then acts as the center line. This quick conversion does not require moving the " +
            "cones and is ideal if one team on this field consists of only 2 children. In this case, the opposing team of three plays with a substitute child in rotation."
    },
    addTeam: {
        title: "Add team",
        clubName: "Club name",
        clubNameShort: "Short club name",
        numberTeams: "Number of trios",
        teamName: "Team name",
        teamStrength: "Team strength",
        addClubDescription: "Club details:",
        addTeamsDescription: "Evaluate the teams:",
        add: "Add",
        next: "Next",
        back: "Back",
        cancel: "Cancel",
        reset: "Reset",
        saved: "Teams added successfully",
        clubNameInputWarning: "Please declare the name of club.",
        clubNameUniqueInputWarning: "This club already exists.",
        clubNameShortInputWarning: "Please give the club a nickname.",
        clubNameShortUniqueInputWarning: "This nickname already exists.",
        numberTeamsInputWarning: "At least 1 team has to join.",
        numberTeamsMaxInputWarning: "The overall maximum number of teams of the same club is 8.",
        teamStrengthInputWarning: "Please evaluate the team strength.",
        addClubNotPossible: "The tournament has started already. New clubs cannot be added anymore.",
        addClubNotPossibleHeader: "Adding club not possible",
        addClubPossibleWithRecalculation: "Adding clubs will reset all scheduled games. Are you sure you want to proceed?",
        confirmEditWithRecalculation: "Yes, add clubs",
        addClubRemovesCurrentGames: "Adding clubs will remove the current games",
    },
    teamTable: {
        title: "Current teams",
        buttonLabel: "Teams",
        columns: {
            club: "Club",
            team: "Team",
            teamStrength: "Team strength",
        }
    },
    editTeam: {
        title: "Teams",
        cancel: "Cancel",
        update: "Update",
        saved: "Successfully updated team",
        clubQuestion: "Choose the respective club first",
        editTeamsNotPossible: "The tournament has been started already. The teams cannot be edited anymore.",
        editTeamsNotPossibleHeader: "Editing teams not possible",
        editTeamsPossibleWithRecalculation: "Editing teams will reset all scheduled games. Are you sure you want to proceed?",
        confirmEditWithRecalculation: "Yes, edit teams",
        editTeamsRemovesCurrentGames: "Editing teams will remove the current games",
        currentGamesRemoved: "The reset was successful",
        resetGamesFailed: "The reset was unsuccessful",
        editTeamsButtonHover: "At least one club must be added"
    },
    settings: {
        title: "Settings",
        design: {
            title: "Design",
            useDarkMode: "Dark Mode",
        },
        language: {
            title: "Language",
            language: "Language",
        },
        other: {
            title: "Other Category",
            something: "Something",
            somethingElse: "Something Else"
        }
    },
    tournamentNotFound: {
        infoText: "This tournament does not exist...",
        buttonText: "Back to home!"
    },
    apiRequests: {
        unspecifiedError: "Oops - something went wrong!"
    },
    imprint: {
        imprint: "Imprint",
        title: "Imprint of d-fine GmbH",
        addressTitle: "Address",
        companyName: "d-fine GmbH",
        address: "An der Hauptwache 7",
        addressZip: "60313 Frankfurt am Main, Deutschland",
        tel: "Tel: {number}",
        fax: "Fax: {number}",
        internet: "Internet: {url}",
        email: "E-Mail: {email}",
        commercialRegister: "Commercial register",
        localCourt: "Local Court: Amtsgericht Frankfurt am Main",
        commercialRegisterNumber: "Commercial register number: Nr. HRB 48 103",
        VATNumber: "VAT identification number DE 813 483 155",
        registeredOffice: "Registered office: Frankfurt",
        management: "Management",
        managementPeople: "Dr. Matthias Aicher, Christian Bangerl, Dr. Florian Baumann, Christoph Belafi, Dr. Christoph Bennemann, Dr. Eike Bick, Dr. Stellan Bohlens, Dr. Oliver Bohr, Dr. Jonas Bräuer, Dr. Christoph Burmester, Dr. Ashot Davtyan, Todor Dobrikov, Dr. Arndt Dombert, Dr. Uwe Dörr, Dr. Benjamin Galow, Dr. Andreas Geyer, Dr. Martin Gläßl, Dr. Robert Görke, Dr. Ferdinand Graf, Dr. Oliver Hein, Dr. Stefan Heinrichs, Dr. Matthias Hirtschulz, Dr. Christian Hörhammer, Dr. Arnd Hübsch, Dr. Sascha Hügle, Dr. Tilman Huhne, Dr. Jan Jureit, Dr. Oliver Kayser-Herold, Dr. Andreas Keese, Dr. Jochen Kienert, Dr. Moritz Kiese, Dr. Fabian Kolley, Dr. Henriette Kröner, Dr. Nikolaus Löbl, Dr. Ulf Menzler, Dr. Florian Merz, Dr. Jochen Meyer, Dr. Karsten Meyer, Dr. Mathias Michel, Christoph Middelkamp, Dr. Cornelius Mund, Dr. Christian Oehler, Dr. Ari Pankiewicz, Wolfgang Pleyer, Torsten Radtke, Dr. Jörn Rank, Dr. Marco Rauch, Dr. Jens Rödiger, Dr. Christian Romeike, Dr. Egbert Schark, Dr. Christoph Schneggenburger, Johanna Schreier, Dr. Sven Schulz, Nadja Schuster, Dr. Markus Seifert, Dr. Thorsten Sickenberger, Ewald Sinkevicius, Dr. Constantin Sobiella, Moritz Sohns, Artur Steiner, Dr. Nico Taschenberger, Dr. Roland Uhlig, Moritz von Medem, Markus von Rothkirch, Dr. Hans-Peter Wächter, Stefan Weißer, Dr. Andreas Werner, Dr. Magnus Wobben"
    },
    dataSecurity: {
        dataSecurity: "Data Safety",
        title: "Data Safety",
        preambleTitle: "Preamble",
        preamble: "Your personal data (e.g., name, address, email address) will only be processed by us in accordance with the provisions of German data protection law and the data protection law of the European Union (EU). The following regulations inform you about the purposes of processing, recipients, legal bases, storage periods, as well as your rights and the party responsible for processing your data.",
        tableOfContents: "Table of Contents",
        toc: [
            "§ 0 Subject of these instructions",
            "§ 1 Controller Responsible for Data Processing",
            "§ 2 Contact Details of our Data Protection Officer",
            "§ 3 Processed Data and Purposes of Data Processing",
            "§ 4 Data Sharing",
            "§ 5 Rights of the Data Subject",
            "§ 6 Contact and Request Management",
            "§ 7 Provision of the Online Services and Web Hosting",
            "§ 8 Use of Cookies",
        ],
        sections: {
            subject: {
                title: "§ 0 Subject of these instructions",
                text: "This privacy notice supplements the general privacy notice of d-fine GmbH and specifically applies to the use of the FALO website and the FALO web application. The general privacy notice of d-fine GmbH can be found at:",
                link: "https://www.d-fine.com/datenschutz/",
                contactPerson: "Contact person for this website (Football Advanced League Organizer – FALO):",
                email: "falo-support[at]d-fine.com"
            },
            responsible: {
                title: "§ 1 Controller Responsible for Data Processing",
                companyName: "d-fine GmbH",
                address: "An der Hauptwache 7",
                addressZip: "60313 Frankfurt am Main, Deutschland",
                tel: "Phone: +49 69 90737-0",
                email: "info[at]d-fine.de",
            },
            dpo: {
                title: "§ 2 Contact Details of our Data Protection Officer",
                address: "An der Hauptwache 7",
                addressZip: "60313 Frankfurt am Main, Deutschland",
                email: "Datenschutzbeauftragter[at]d-fine.de",
            },
            processing: {
                first: {
                    title: "§ 3 Processed Data and Purposes of Data Processing",
                    text: "**Registration of the tournament management:** In order to use the Football Advanced League Organizer (FALO), the tournament management must create an account. The following information is collected:",
                    Name: "Name",
                    email: "Email address",
                    clubName: "Club name",
                    password: "Password",
                    datalaw: "This data is required exclusively to access the application. Legal basis: Performance of a contract pursuant to Art. 6 (1) lit. b GDPR.",
                },
                second: {
                    text: "**Creating and sharing tournaments:** Within the FALO application, the tournament management can create tournaments in which publicly available club names and club addresses are entered. The tournaments as well as the results of the matches can be shared via a public link (e.g., with parents or coaches). However, no personal data is displayed. Legal basis: Legitimate interests pursuant to Art. 6 (1) lit. f GDPR (provision of the application and its functionalities).",
                },
                third: {
                    text: "**Logging and log files:** For technical and security reasons, access data (including IP addresses) is temporarily stored in the form of server log files. These include:",
                    timepoint: "Access time",
                    ipAddress: "IP address",
                    browserInfo: "Browser information",
                    timeStored: "The log files are stored for a maximum of 30 days and then deleted, unless they must be temporarily retained to clarify security incidents.",
                    datalaw: "Legal basis: Legitimate interests pursuant to Art. 6 (1) lit. f GDPR (ensuring operation and security).",
                },
                fourth: {
                    textTitle: "Deletion of data:",
                    text: "All personal data (including tournament data) will be completely deleted no later than the end of the pilot phase in late 2026.",
                },
            },
            sharing: {
                title: "§ 4 Data Sharing",
                text: "The collected data will not be passed on to third parties (except to the service provider mentioned in § 7 Number 5). Internal further processing of the data also does not take place, except to ensure the functionality of the application and to determine match rounds (e.g., based on match results).",
            },

            rights: {
                title: "§ 5 Rights of the Data Subject",
                text: "As a data subject, you have various rights under the GDPR, in particular arising from Articles 15 to 21 GDPR. Please use the contact email address for this purpose:",
                contactEmail: "falo-support[at]d-fine.com",
                objection: "Right to object: You have the right, for reasons arising from your particular situation, to object at any time to the processing of your personal data carried out pursuant to Art. 6 (1) lit. e or f GDPR; this also applies to profiling based on these provisions. If your personal data are processed for direct marketing purposes, you have the right to object at any time to the processing of your personal data for such advertising purposes; this also applies to profiling to the extent it is related to such direct marketing.",
                revokeConsent: "Right to withdraw consent: You have the right to withdraw any consent you have given at any time.",
                rightOfAccess: "Right of access: You have the right to obtain confirmation as to whether data concerning you are being processed, as well as access to this data and further information and a copy of the data in accordance with the legal requirements.",
                rectification: "Right to rectification: You have the right, in accordance with the legal provisions, to request completion of the data concerning you or correction of inaccurate data concerning you.",
                deletionRestriction: "Right to erasure and restriction of processing: You have the right, in accordance with the legal provisions, to request that data concerning you be deleted immediately, or alternatively, to request a restriction of the processing of such data.",
                portability: "Right to data portability: You have the right to receive data concerning you that you have provided to us, in a structured, commonly used, and machine-readable format, or to request their transfer to another controller in accordance with the legal provisions.",
                complaint: "Right to lodge a complaint with a supervisory authority: Without prejudice to any other administrative or judicial remedy, you have the right to lodge a complaint with a supervisory authority, in particular in the member state of your habitual residence, your workplace, or the location of the alleged infringement, if you consider that the processing of your personal data violates the GDPR."
            },
            contact: {
                title: "§ 6 Contact and Request Management",
                text: "When contacting us (e.g., by email), the information provided by the person making the inquiry is processed to the extent necessary to respond to the contact request and any requested actions.",
                dataTypes: "Types of data processed: Contact data (e.g., email, phone numbers); content data (e.g., inputs in online forms); usage data (e.g., visited websites, interest in content, access times); meta, communication, and process data (e.g., IP addresses, timestamps, identification numbers, consent status).",
                legalBasis: "Legitimate interests (Art. 6 (1) sentence 1 lit. f GDPR); contract performance and pre-contractual requests (Art. 6 (1) sentence 1 lit. b GDPR)"
            },
            onlineProvision: {
                title: "§ 7 Provision of the Online Services and Web Hosting",
                text: "We process user data in order to provide them with our online services. For this purpose, we process the user's IP address, which is necessary to deliver the content and functions of our online services to the user's browser or device.",
                dataTypes: "Types of data processed: Usage data (e.g., visited websites, interest in content, access times); meta, communication, and process data (e.g., IP addresses, timestamps, identification numbers, consent status).",
                purposes: "Purposes of processing: Provision of our online services and user-friendliness; IT infrastructure (operation and provision of information systems and technical devices, e.g., computers, servers); security measures.",
                legalBasis: "Legal basis: Legitimate interests (Art. 6 (1) sentence 1 lit. f GDPR).",
                //additionalInfo: "Further information on processing procedures, processes, and services:",
                infoCollection: "Collection of access data and log files: Access to our online services is logged in the form of so-called server log files. Server log files may include the address and name of the accessed websites and files, date and time of access, transmitted data volume, notice of successful retrieval, browser type and version, user's operating system, referrer URL (previously visited page), and usually IP addresses and the requesting provider. Server log files can be used for security purposes, e.g., to prevent server overload (particularly in the case of abusive attacks, so-called DDoS attacks) and to ensure server performance and stability. Legal basis: Legitimate interests (Art. 6 (1) sentence 1 lit. f GDPR). Deletion of data: Log file information is stored for a maximum of 30 days and then deleted or anonymized. Data that must be retained for evidentiary purposes are excluded from deletion until the relevant incident is fully resolved.",
                ovhInfo: "OVH: Services in the field of providing IT infrastructure and related services (e.g., storage and/or computing capacities); service provider: OVH GmbH, Oskar-Jäger-Str. 173/K6, 50825 Köln, Deutschland; Legal basis: Legitimate interests (Art. 6 (1) sentence 1 lit. f GDPR); Website: {website}; Privacy Policy: {privacy}; Data Processing Agreement: {dpa}",
                euHosting: "All data processed through this website and the FALO web application is stored and processed exclusively in data centers located within the European Union. The service is hosted on infrastructure provided by OVH GmbH (Germany/EU).",
                noTracking: "No web analytics tools, tracking mechanisms, social media plugins, or other third-party services are used for analytical or marketing purposes. No profiling is carried out within the meaning of Article 4(4) of the GDPR."
            },
            cookies: {
                title: "§ 8 Use of Cookies",
                text: "Cookies are small text files or other storage notes that store information on devices and read information from the devices. Cookies can be used for various purposes, e.g., for the functionality, security, and convenience of online services, as well as for analyzing visitor behavior.",
                consentRequired: "We use cookies in accordance with legal requirements. Therefore, we obtain prior consent from users unless it is not legally required. Consent is particularly not necessary if storing and reading the information, including cookies, is strictly necessary to provide users with an expressly requested telemedia service (i.e., our online offering). Strictly necessary cookies typically include cookies for displaying and running the online service, load balancing, security, storing user preferences and choices, or similar purposes associated with providing the main and additional functions of the online service requested by the user. The revocable consent is clearly communicated to users and contains information about the respective use of cookies.",
                legalBasis: "Information on legal bases under data protection law: The legal basis for processing users' personal data using cookies depends on whether we request consent from users. If users consent, the legal basis for processing is the declared consent. Otherwise, the data processed via cookies are processed based on our legitimate interests (e.g., business operation of our online services and improving usability) or, if necessary for fulfilling contractual obligations, where the use of cookies is required to meet our contractual duties. The purposes for which cookies are processed are explained throughout this privacy policy or within our consent and processing procedures.",
                storageDuration: "Storage duration: Regarding storage duration, the following types of cookies are distinguished: a) Temporary cookies (session cookies): Temporary cookies are deleted as soon as a user leaves an online service and closes their device (e.g., browser or mobile application); b) Persistent cookies: Persistent cookies remain stored even after closing the device. For example, login status can be saved or preferred content displayed when the user visits a website again. Data collected via cookies may also be used to measure reach. If we do not provide explicit information on cookie types and storage duration (e.g., during consent collection), users should assume cookies are persistent and may be stored for up to two years.",
                optOut: "General information on revocation and objection (so-called 'opt-out'): Users can revoke previously given consents at any time and object to processing in accordance with legal requirements. Users can, for example, restrict cookie use in their browser settings (which may also limit the functionality of our online services)."
            },
        },
    },
    feedback: {
        feedbackTitle: "Feedback",
        textBefore: "Did you find a bug? Something not quite right, or do you think the FALO software could be more user-friendly? Then feel free to send us an email at",
        email: "falo-support[at]d-fine.com",
        textAfter: "We are in an early development phase of the Football Advanced League Organizer and welcome any feedback.",
    },
}